import React from 'react';
import { Radio, Stack, Box, Slider, Center, Text, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IJournalResponseInput } from '../../../../global/entities/Journal/Journal';

// Emoji mapping for scale values 0-10
const getEmojiForValue = (value: number, isReversed: boolean = false): string => {
    const emojis = ['😢', '😟', '😞', '😐', '😔', '😐', '🙂', '😊', '😄', '😁', '🤩'];
    
    if (isReversed) {
        // Reverse the emoji array so value 0 gets the happiest emoji
        const reversedEmojis = [...emojis].reverse();
        return reversedEmojis[Math.round(Math.max(0, Math.min(10, value)))] || '😐';
    }
    
    return emojis[Math.round(Math.max(0, Math.min(10, value)))] || '😐';
};

interface YesNoScaleQuestionProps {
    questionId: number;
    response: IJournalResponseInput | undefined;
    onResponseChange: (questionId: number, updates: Partial<IJournalResponseInput>) => void;
    readOnly?: boolean;
    isReversed?: boolean;
}

export const YesNoScaleQuestion: React.FC<YesNoScaleQuestionProps> = ({
    questionId,
    response,
    onResponseChange,
    readOnly = false,
    isReversed = false
}) => {
    const { t } = useTranslation();
    
    const primaryResponse = response?.responseValue;
    const scaleValue = response?.subResponseValue ? parseFloat(response.subResponseValue) : 0;

    // Normalize the response value to lowercase to match radio button values
    const normalizedValue = primaryResponse?.toLowerCase() || '';

    return (
        <Stack gap="md">
            {/* Primary yes/no question */}
            <Radio.Group
                value={normalizedValue}
                onChange={readOnly ? undefined : (value) => {
                    onResponseChange(questionId, { 
                        responseValue: value,
                        subResponseValue: value === 'yes' ? '0' : ''
                    });
                }}
            >
                <Group gap="md">
                    <Radio 
                        value="yes" 
                        label={t('journal.yes')}
                    />
                    <Radio 
                        value="no" 
                        label={t('journal.no')}
                    />
                </Group>
            </Radio.Group>

            {/* Scale question if yes */}
            {normalizedValue === 'yes' && (
                <Box p="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)', borderRadius: 4 }}>
                    <Center mb="xl" mt="md">
                        <Group gap="md">
                            <Text size="2rem" style={{ lineHeight: 1 }}>{getEmojiForValue(scaleValue, isReversed)}</Text>
                            <Text fw={700} size="2rem" style={{ lineHeight: 1 }}>{scaleValue}</Text>
                        </Group>
                    </Center>
                    
                    <Box>
                        <Slider
                            value={scaleValue}
                            onChange={readOnly ? undefined : (value) => onResponseChange(questionId, { subResponseValue: value.toString() })}
                            min={0}
                            max={10}
                            step={1}
                            marks={Array.from({ length: 10 - 0 + 1 }, (_, i) => {
                                const markValue = 0 + i;
                                return {
                                    value: markValue,
                                    label: getEmojiForValue(markValue, isReversed)
                                };
                            })}
                            mb="md"
                        />
                    </Box>
                </Box>
            )}
        </Stack>
    );
}; 